import { ref, computed, onMounted, watch, type Ref } from 'vue'
import { useStore } from 'vuex'
import validateReviewEditorialView from '@/services/validation/reviewEditorial'
import { trackEvent } from '@/services/gtm'
import {
  EditorialOffice,
  EditorialOfficeSelectionMode,
  GTMEventPayload,
  EditorialActionPayload,
  VerifyResult,
} from '@/types'

/**
 * Shared fetch state across all composable instances.
 *
 * Ensures:
 * - Editorial offices are fetched only once
 * - Fetch errors are shared globally
 * * Prevents duplicate API calls if multiple components use this composable
 */
const sharedEditorialOfficeFetchFailed = ref(false)
const sharedEditorialOfficeFetchError = ref<unknown | null>(null)
let hasFetchedEditors = false

/**
 * Composable for managing editorial office functionality.
 *
 * Responsibilities:
 * - Fetch editorial offices (once globally)
 * - Manage selected editorial offices
 * - Enforce business rules (max 2 editors)
 * - Handle editorial selection mode changes
 * - Integrate GTM tracking
 * - Trigger validation after state mutations
 * - Track server-side errors
 *
 * This centralises editorial-related logic to:
 * - Reduce duplication
 * - Keep components lightweight
 * - Standardise tracking + validation behaviour
 *
 * @returns Reactive properties and methods for editorial office management
 */
export function useEditorialManagement(canDisplayErrors?: Ref<boolean>) {
  const store = useStore()

  // --------------------------------------------------
  // Computed - Editorial Data
  // --------------------------------------------------

  /**
   * All editorial offices retrieved from Vuex state.
   */
  const allEditors = computed<EditorialOffice[]>(() => store.state.editorialOffices || [])

  /**
   * Currently selected editorial offices.
   *
   * Getter:
   * - Maps stored IDs to full EditorialOffice objects
   *
   * Setter:
   * - Replaces selection in Vuex (used for reorder operations)
   */
  const selectedEditors = computed<EditorialOffice[]>({
    get: () =>
      store.state.currentlySelectedEditorialOffices
        .map((id: string) => allEditors.value.find(e => e.peerReviewIdentifier === id))
        .filter(Boolean) as EditorialOffice[],
    set: (editors: EditorialOffice[]) => {
      store.dispatch('setSelectedEditorialOffices', editors)
    },
  })

  /**
   * Controls how editorial offices are selected.
   *
   * When changed:
   * - Updates Vuex state
   * - Tracks GTM event
   * - Triggers validation
   */
  const editorialSelectionMode = computed<EditorialOfficeSelectionMode>({
    get: () => store.state.editorialOfficeSelectionMode,
    set: async (mode) => {
      await performActionWithTracking('setEditorialOfficeSelectionMode', mode, {
        action: 'change_mode',
        event: 'EditorialSelectionModeChanged',
        editorialOfficeSelectionMode: mode,
      })
    },
  })

  /**
   * Determines whether the editorial office selector UI should be displayed.
   */
  const showEditorialOffices = computed<boolean>(
    () => editorialSelectionMode.value === EditorialOfficeSelectionMode.AuthorDecided,
  )

  const isAssociateEditorSelectionInvalid = computed(() =>
    showEditorialOffices.value && selectedEditors.value.length !== 2,
  )

  const verifyResult = computed<VerifyResult | null>(() =>
    store.getters.getVerifyResultByName('reviewEditorialVerifyResult'),
  )

  const failedRules = computed(() => verifyResult.value?.failedRules ?? {})

  const canProgress = computed(() => verifyResult.value?.valid ?? false)

  const showErrors = computed(() =>
    (verifyResult.value?.errors?.length ?? 0) > 0 && canDisplayErrors?.value,
  )

  const shouldShowAssociateEditorError = computed(() =>
    canDisplayErrors?.value && isAssociateEditorSelectionInvalid.value,
  )

  // --------------------------------------------------
  // Shared Fetch State
  // --------------------------------------------------

  /**
   * Indicates whether fetching editorial offices failed.
   */
  const editorialOfficeFetchFailed = sharedEditorialOfficeFetchFailed

  /**
   * Stores fetch error details if request fails.
   */
  const editorialOfficeFetchError = sharedEditorialOfficeFetchError

  // --------------------------------------------------
  // Utilities
  // --------------------------------------------------

  /**
   * Wrapper utility for Vuex actions requiring:
   * - Dispatch
   * - GTM tracking
   * - Post-action validation
   *
   * Centralises cross-cutting concerns to avoid duplication.
   *
   * @param vuexAction - Vuex action name
   * @param payload - Payload for the action
   * @param gtmEvent - GTM event metadata
   */
  async function performActionWithTracking(
    vuexAction: string,
    payload: EditorialActionPayload,
    gtmEvent: GTMEventPayload,
  ): Promise<void> {
    await store.dispatch(vuexAction, payload)
    trackEvent({ category: 'Editorial', ...gtmEvent })
    await validateReviewEditorialView(store)
  }

  // --------------------------------------------------
  // Editorial CRUD Operations
  // --------------------------------------------------

  /**
   * Adds a new editorial office to the selection.
   *
   * Business rules:
   * - Maximum of 2 editors allowed
   *
   * @param editor - Editorial office to add
   */
  const addEditor = async (editor: EditorialOffice) => {
    if (selectedEditors.value.length >= 2) return
    if (selectedEditors.value.some(e => e.peerReviewIdentifier === editor.peerReviewIdentifier)) return
    await performActionWithTracking('addEditorialOffice', editor, {
      action: 'select',
      event: 'EditorSelected',
      editorId: editor.peerReviewIdentifier,
    })
  }

  /**
   * Removes an editorial office from selection.
   *
   * @param editorId - Identifier of editorial office to remove
   */
  const removeEditor = async (editorId: string) => {
    await performActionWithTracking('removeEditorialOffice', editorId, {
      action: 'remove',
      event: 'EditorRemoved',
      editorId,
    })
  }

  /**
   * Reorders selected editorial offices.
   *
   * Used for drag-and-drop functionality.
   *
   * @param editors - Fully ordered list of selected offices
   */
  const reorderEditors = async (editors: EditorialOffice[]) => {
    await performActionWithTracking('setSelectedEditorialOffices', editors, {
      action: 'reorder',
      event: 'EditorsReordered',
      editors: editors.map(e => e.peerReviewIdentifier),
    })
  }

  /**
   * High-level editor selection handler for UI interaction.
   *
   * - Adds editor if max limit not reached
   * - Optionally executes scroll callback
   *
   * @param editor - Editorial office to select
   * @param scrollFn - Optional scroll function
   */
  async function selectEditor(editor: EditorialOffice, scrollFn?: () => void) {
    if (selectedEditors.value.length < 2) {
      await addEditor(editor)
      if (scrollFn) scrollFn()
    }
  }

  // --------------------------------------------------
  // Lifecycle
  // --------------------------------------------------

  /**
   * Fetch editorial offices once on first composable mount.
   *
   * Uses shared state to prevent duplicate API calls.
   */
  onMounted(() => {
    if (!hasFetchedEditors) {
      hasFetchedEditors = true
      store.dispatch('fetchEditorialOffices').catch((err) => {
        sharedEditorialOfficeFetchFailed.value = true
        sharedEditorialOfficeFetchError.value = err
      })
    }
  })

  /**
   * Tracks server errors related to editorial office operations.
   *
   * @param error - Error object thrown during request
   */
  function trackAssociateEditorsError(error: unknown) {
    const err = error as { response?: { status?: number }, message?: string }

    trackEvent({
      category: 'Server Errors',
      action: 'error',
      event: 'associateEditorsError',
      errorType: err.response?.status ? `HTTP${err.response.status}` : 'UnknownError',
      errorDetails: err.message || 'No error message available',
      submissionId: store.state.activeSubmission.submissionId,
    })
  }

  /**
   * Automatically track fetch errors when they occur.
   */
  watch(editorialOfficeFetchError, (error) => {
    if (error) trackAssociateEditorsError(error)
  })

  // --------------------------------------------------
  // Return API
  // --------------------------------------------------

  return {
    // Computed
    selectedEditors,
    allEditors,
    editorialSelectionMode,
    showEditorialOffices,
    isAssociateEditorSelectionInvalid,

    verifyResult,
    failedRules,
    canProgress,
    showErrors,
    shouldShowAssociateEditorError,

    // Fetch State
    editorialOfficeFetchFailed,
    editorialOfficeFetchError,

    // CRUD Methods
    addEditor,
    removeEditor,
    reorderEditors,
    selectEditor,

    // Utilities
    trackAssociateEditorsError,
  }
}
